import React, { useEffect, useMemo, useState } from "react";

import {
  Accordion,
  Button,
  Col,
  Container,
  Image,
  Modal,
  Row,
} from "react-bootstrap";
import Gallery from "react-image-gallery";

import { getCurrentLang } from "@/utils/Local";

import i18n from "../i18n";
import ErrorComponent from "./Error.component";
import LoaderComponent from "./Loader.component";
import useEvents from "../hooks/useEvents";

import type { IEvent, IEventImage } from "@/interfaces/IEvent";
import type { IGalleryImage } from "@/interfaces/IGalleryImage";

const EventsComponent = () => {
  const { t } = i18n;
  const lang = getCurrentLang();
  const [showGallery, setShowGallery] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  // always show the first 5 events
  const [activeKeys, setActiveKeys] = useState<string[]>([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
  ]);
  const [galleryImages, setGalleryImages] = useState<IGalleryImage[]>([]);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useEvents();

  const events = useMemo(
    () => data?.pages.flatMap((page) => page.events) ?? [],
    [data?.pages],
  );

  const openGallery = (categoryIndex: number, imgIndex: number) => {
    // load images
    const images: IGalleryImage[] = [];
    events[categoryIndex].images.map((img, index) => {
      images.push({
        original: img.original,
        thumbnail: img.thumbnail,
        description: `${events[categoryIndex].title[lang]} (${index + 1})`,
        alt: `${events[categoryIndex].title[lang]} (${index + 1})`,
      });
      return null;
    });
    setGalleryImages(images);
    setCurrentIndex(imgIndex);
    setShowGallery(true);
  };

  // Toggle accordion expansion
  const handleToggle = (key: string) => {
    setActiveKeys(
      (prevKeys) =>
        prevKeys.includes(key)
          ? prevKeys.filter((k) => k !== key) // Remove key to collapse
          : [...prevKeys, key], // Add key to expand
    );
  };

  useEffect(() => {
    if (data?.pages.length) {
      const latestPage = data.pages[data.pages.length - 1];
      const latestItemKey = events.length - latestPage.events.length;
      setActiveKeys((prevKeys) => [...prevKeys, latestItemKey.toString()]); // Expand the latest item
    }
  }, [data?.pages, events]);

  if (isFetching) {
    return <LoaderComponent standalone={false} />;
  }

  if (error) {
    return <ErrorComponent standalone={false} />;
  }

  if (!data) {
    return <React.Fragment>l</React.Fragment>;
  }

  return (
    <React.Fragment>
      <Modal
        fullscreen
        className="modalGallery"
        data-bs-theme="dark"
        onHide={() => setShowGallery(false)}
        show={showGallery}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <Gallery
            items={galleryImages}
            showThumbnails={false}
            startIndex={currentIndex}
          />
        </Modal.Body>
      </Modal>

      <Accordion alwaysOpen activeKey={activeKeys}>
        {events.map((event: IEvent, index: number) => (
          <Accordion.Item key={index} eventKey={index.toString()}>
            <Accordion.Header onClick={() => handleToggle(index.toString())}>
              {event.title[lang]}
            </Accordion.Header>
            <Accordion.Body>
              <Container>
                <Row>
                  {event.images.map((image: IEventImage, imgIndex: number) => (
                    <Col
                      key={imgIndex}
                      className="mb-4 position-relative text-center"
                      md={4}
                      xs={6}
                    >
                      <Image
                        alt={`${event.title[lang]} (${imgIndex + 1})`}
                        className="mw-100"
                        loading="lazy"
                        onClick={() => openGallery(index, imgIndex)}
                        role="button"
                        src={image.thumbnail}
                      />
                    </Col>
                  ))}
                </Row>
              </Container>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {hasNextPage ? (
        <Row>
          <Col className="text-center mt-4" xs={12}>
            <Button
              className="btn-outline"
              disabled={isFetchingNextPage}
              onClick={async () => fetchNextPage().then(() => {})}
              size="sm"
              variant="outline-success ps-4 pe-4"
            >
              {t("load_more")}
            </Button>
          </Col>
        </Row>
      ) : null}
    </React.Fragment>
  );
};

export default EventsComponent;
